/**
 * CricCam Pro — TV Broadcast Lower-Third Popup Engine
 * Detects player milestones (50s/100s, 3/5 wkts), partnerships, over summaries, and chase equations.
 * Uses celebration guards to ensure milestone popups fire EXACTLY ONCE per milestone.
 */

// key -> the innings ball count at which it was celebrated. A Map (not a Set) so
// an Undo can be detected: if the innings has rewound to an earlier ball than the
// one that produced the celebration, the milestone is eligible to fire again.
const celebratedMilestones = new Map();

/**
 * Reset celebration memory for a new match/session
 */
export function resetCelebratedMilestones() {
  celebratedMilestones.clear();
}

function alreadyCelebrated(key, balls) {
  const at = celebratedMilestones.get(key);
  if (at === undefined) return false;
  return balls >= at; // rewound behind the celebration point → allow it again
}

function markCelebrated(key, balls) {
  celebratedMilestones.set(key, balls);
}

/**
 * Check if the latest ball triggered a lower-third broadcast popup
 * @returns {object|null} Payload for LowerThirdPopup component or null
 */
export function checkLowerThirdTrigger(ball, matchContext = {}) {
  if (!ball || !matchContext) return null;

  const { striker, bowler, inn, match } = matchContext;

  // Celebrations are keyed by player+milestone and remembered together with the
  // ball count at which they fired, so an Undo makes them eligible again.
  const inningsIdx = Array.isArray(match?.innings) ? match.innings.length : 0;
  const balls = Number(inn?.balls || 0);
  const scope = `i${inningsIdx}`;

  // Prune old entries to prevent memory leak across matches/innings
  for (const key of celebratedMilestones.keys()) {
    if (!key.includes(scope)) {
      celebratedMilestones.delete(key);
    }
  }

  // 1. BATTER MILESTONE (50 or 100 Runs) — Triggers EXACTLY ONCE per milestone
  if (striker && striker.name) {
    const runs = Number(striker.runs || 0);
    const keyFifty = `fifty_${scope}_${striker.name}`;
    const keyHundred = `hundred_${scope}_${striker.name}`;

    if (runs >= 50 && runs < 100 && !alreadyCelebrated(keyFifty, balls) && (ball.runs > 0 || ball.type === 'normal')) {
      markCelebrated(keyFifty, balls);
      return {
        type: 'milestone',
        title: `50 RUNS MILESTONE`,
        subtitle: `${striker.name} (${striker.runs} off ${striker.balls}b | ${striker.fours || 0}x4, ${striker.sixes || 0}x6)`,
        badge: '💥 FIFTY',
        color: '#fbbf24',
        durationMs: 5000
      };
    }

    if (runs >= 100 && !alreadyCelebrated(keyHundred, balls) && (ball.runs > 0 || ball.type === 'normal')) {
      markCelebrated(keyHundred, balls);
      return {
        type: 'milestone',
        title: `100 RUNS MILESTONE`,
        subtitle: `${striker.name} (${striker.runs} off ${striker.balls}b | ${striker.fours || 0}x4, ${striker.sixes || 0}x6)`,
        badge: '💯 CENTURY',
        color: '#fbbf24',
        durationMs: 5000
      };
    }
  }

  // 2. BOWLER MILESTONE (3 or 5 Wickets) — Triggers EXACTLY ONCE
  if (bowler && bowler.name) {
    const wkts = Number(bowler.wickets || 0);
    const keyThree = `bowler_3_${scope}_${bowler.name}`;
    const keyFive = `bowler_5_${scope}_${bowler.name}`;

    if (wkts >= 3 && wkts < 5 && !alreadyCelebrated(keyThree, balls) && (ball.type === 'wicket' || ball.wicketType)) {
      markCelebrated(keyThree, balls);
      return {
        type: 'milestone',
        title: `3 WICKETS HAUL`,
        subtitle: `${bowler.name} (${bowler.wickets}-${bowler.runs} in ${Math.floor(bowler.balls / 6)}.${bowler.balls % 6} ov)`,
        badge: '⚡ 3-WKT HAUL',
        color: '#ef4444',
        durationMs: 5000
      };
    }

    if (wkts >= 5 && !alreadyCelebrated(keyFive, balls) && (ball.type === 'wicket' || ball.wicketType)) {
      markCelebrated(keyFive, balls);
      return {
        type: 'milestone',
        title: `5 WICKETS HAUL`,
        subtitle: `${bowler.name} (${bowler.wickets}-${bowler.runs} in ${Math.floor(bowler.balls / 6)}.${bowler.balls % 6} ov)`,
        badge: '🔥 5-WKT HAUL',
        color: '#ef4444',
        durationMs: 5000
      };
    }
  }

  // 3. OVER SUMMARY (Triggered at end of over, e.g. ball % 6 === 0)
  if (inn && inn.balls > 0 && inn.balls % 6 === 0 && ball.isLegal !== false) {
    const overNum = Math.floor(inn.balls / 6);
    const lastOverBalls = (inn.ballLog || []).filter((b, i) => (b.over !== undefined ? b.over : Math.floor(i / 6)) === overNum - 1);
    const runsThisOver = lastOverBalls.reduce((s, b) => s + (b.runs || 0), 0);
    const wktsThisOver = lastOverBalls.filter(b => b.type === 'wicket' || b.wicketType).length;
    const crr = inn.balls > 0 ? ((inn.totalRuns / inn.balls) * 6).toFixed(2) : '0.00';

    return {
      type: 'overSummary',
      title: `END OF OVER ${overNum}`,
      subtitle: `${runsThisOver} Runs, ${wktsThisOver} Wkt | Score: ${inn.totalRuns}/${inn.wickets} (CRR ${crr})`,
      badge: `OVER ${overNum}`,
      color: '#6ee591',
      durationMs: 4500
    };
  }

  // 4. CHASE EQUATION (2nd Innings)
  if (match && Array.isArray(match.innings) && match.innings.length >= 2 && inn) {
    const inn1 = match.innings[0] || {};
    const target = Number(inn1.totalRuns || 0) + 1;
    const currentRuns = Number(inn.totalRuns || 0);
    const needed = target - currentRuns;
    const totalMatchBalls = Number(match.overs || 20) * 6;
    const remainingBalls = totalMatchBalls - Number(inn.balls || 0);

    if (needed > 0 && remainingBalls > 0 && (ball.runs === 4 || ball.runs === 6)) {
      const battingTeamName = inn.battingTeam === 'A' ? (match.teamA?.name || 'Team A') : (match.teamB?.name || 'Team B');
      const rrr = ((needed / remainingBalls) * 6).toFixed(2);
      return {
        type: 'chaseEquation',
        title: `${battingTeamName} CHASE EQUATION`,
        subtitle: `Need ${needed} runs in ${remainingBalls} balls (RRR ${rrr})`,
        badge: 'TARGET CHASE',
        color: '#38bdf8',
        durationMs: 4000
      };
    }
  }

  // 5. PLAYER CARD (New Batter or New Bowler)
  if (matchContext.enablePlayerCards) {
    if (striker && striker.name && striker.balls <= 1) {
      const keyName = `playercard_batter_${scope}_${striker.name}`;
      if (!alreadyCelebrated(keyName, balls)) {
        markCelebrated(keyName, balls);
        return {
          type: 'playerCard',
          title: `🏏 NEW BATTER: ${striker.name.toUpperCase()}`,
          subtitle: `Match Stats: ${striker.runs} runs off ${striker.balls} balls`,
          badge: 'PLAYER PROFILE',
          color: '#3b82f6',
          durationMs: 5000
        };
      }
    }
    if (bowler && bowler.name && bowler.balls <= 1) {
      const spellKey = `playercard_bowler_${scope}_${bowler.name}`;
      if (!alreadyCelebrated(spellKey, balls)) {
        markCelebrated(spellKey, balls);
        return {
          type: 'playerCard',
          title: `🎯 NEW BOWLER: ${bowler.name.toUpperCase()}`,
          subtitle: `Match Stats: ${bowler.wickets} wkts for ${bowler.runs} runs`,
          badge: 'PLAYER PROFILE',
          color: '#8b5cf6',
          durationMs: 5000
        };
      }
    }
  }

  return null;
}
